// frontend/src/api/valuationNormalization.ts
//
// VAL-U2C-SCREEN-1 — typed client for the Review and adjust step. Resolves to
// apps/api/routers/_valuation_normalization.py. No figure is computed here: the
// per-year earnings, the basis and the rule sentence are the server's.

import { apiClient } from '@/api/client'

const base = (id: string) => `/v1/valuation/engagements/${id}/normalization`

export type AddBackCategory = 'owner_comp' | 'one_time' | 'non_operating'

/** One add-back as stored, with who and when. */
export interface AddBack {
  id: string
  category: AddBackCategory
  year: number
  amount: number
  reason: string
  description?: string | null
  is_pending: boolean
  removed?: boolean
  created_by?: string
  created_at?: string
  updated_by?: string
  updated_at?: string
}

/** One fiscal year's normalized figures; null when the row cannot support them. */
export interface NormalizedYear {
  year: number
  ebitda: number | null
  adjusted_ebitda: number | null
  owner_comp_total: number | null
  sde: number | null
}

export interface NormalizationOk {
  rule_text: string
  years: NormalizedYear[]
  basis: string
  normalized_earnings: number
  averaged: { sde: number; adjusted_ebitda: number }
}

/** The server's plain-English reason there is no result yet. */
export interface NormalizationError { error: string }

export interface BalanceSheet {
  total_assets: number
  total_liabilities: number
  as_of_year?: number | null
  updated_by?: string
  updated_at?: string
}

export interface NormalizationView {
  valuation_engagement_id: string
  years: number[]
  add_backs: AddBack[]
  removed_count: number
  result: NormalizationOk | NormalizationError
  balance_sheet: BalanceSheet | null
}

export interface AddBackBody {
  id?: string
  category: AddBackCategory
  year: number
  amount: number
  reason: string
  description?: string
  is_pending: boolean
}

export interface ApplyResponse {
  applied: { normalized_earnings: number; basis: string; total_assets?: number; total_liabilities?: number }
  rule_text: string
  updated_by: string
}

export async function fetchNormalization(id: string): Promise<NormalizationView> {
  const { data } = await apiClient.get<NormalizationView>(base(id))
  return data
}

export async function saveAddBack(id: string, body: AddBackBody): Promise<{ add_back: AddBack }> {
  const { data } = await apiClient.put(`${base(id)}/add-backs`, body)
  return data
}

export async function removeAddBack(id: string, addBackId: string): Promise<{ removed: string }> {
  const { data } = await apiClient.delete(`${base(id)}/add-backs/${addBackId}`)
  return data
}

export async function saveBalanceSheet(
  id: string,
  body: { total_assets: number; total_liabilities: number; as_of_year?: number | null },
): Promise<{ balance_sheet: BalanceSheet | null }> {
  const { data } = await apiClient.put(`${base(id)}/balance-sheet`, body)
  return data
}

export async function applyNormalization(id: string): Promise<ApplyResponse> {
  const { data } = await apiClient.post<ApplyResponse>(`${base(id)}/apply`)
  return data
}
